<script setup lang="ts">
import type { BlogPost } from "~/types/blog";

// `content` never reaches the card — `/api/blog` strips it from the list payload.
defineProps<{
  post: Omit<BlogPost, "content">;
}>();

const { formatPostDate } = useBlogFormat();
</script>

<template>
  <article class="post-card">
    <NuxtLink
      class="post-card__link"
      :to="`/blog/${post.slug}`"
    >
      <!--
        Plain <img>, not <NuxtImg>: the images are hosted on whatever CDN Soro
        writes into the feed, and @nuxt/image only optimises hosts listed in
        `image.domains`. An unlisted host throws at render, so the optimiser is
        the wrong tool for a URL we don't control.
      -->
      <div class="post-card__media">
        <img
          v-if="post.image"
          :alt="post.title"
          decoding="async"
          height="360"
          loading="lazy"
          :src="post.image"
          width="640"
        >
        <div
          v-else
          aria-hidden="true"
          class="post-card__placeholder"
        >
          <fa :icon="['fal', 'book']" />
        </div>
      </div>

      <div class="post-card__body">
        <p
          v-if="post.categories.length"
          class="post-card__category"
        >
          {{ post.categories[0] }}
        </p>

        <h2 class="post-card__title">
          {{ post.title }}
        </h2>

        <p
          v-if="post.excerpt"
          class="post-card__excerpt"
        >
          {{ post.excerpt }}
        </p>

        <p class="post-card__meta">
          <time
            v-if="post.publishedAt"
            :datetime="post.publishedAt"
          >
            {{ formatPostDate(post.publishedAt) }}
          </time>
          <span
            v-if="post.publishedAt"
            aria-hidden="true"
          >·</span>
          <span>{{ post.readingMinutes }} min read</span>
        </p>
      </div>
    </NuxtLink>
  </article>
</template>

<style lang="scss" scoped>
.post-card {
  background: var(--color-white-1);
  border: 1px solid var(--color-grey-3);
  border-radius: var(--radius-lg);
  overflow: hidden;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 0.125rem 0.5rem rgb(0 0 0 / 8%);
  }

  &__link {
    display: flex;
    flex-direction: column;
    height: 100%;
    color: var(--color-grey-1);
    font-weight: var(--weight-regular);
    text-decoration: none;

    &:hover {
      color: var(--color-grey-1);
      text-decoration: none;
    }
  }

  &__media {
    aspect-ratio: 16 / 9;
    background: var(--color-grey-4);

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__placeholder {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    color: var(--color-grey-2);
    font-size: var(--text-2xl);
  }

  &__body {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-4);
  }

  &__category {
    color: var(--color-grey-2);
    font-size: var(--text-xs);
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  &__title {
    font-size: var(--text-xl);
    font-weight: var(--weight-semibold);
    line-height: 1.3;

    .post-card__link:hover & {
      color: var(--color-pink-1);
    }
  }

  &__excerpt {
    color: var(--color-grey-2);
    font-size: var(--text-sm);
    line-height: 1.6;
  }

  &__meta {
    display: flex;
    gap: var(--space-1);
    margin-top: auto;
    padding-top: var(--space-2);
    color: var(--color-grey-2);
    font-size: var(--text-xs);
  }
}
</style>
